import { useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Cog, Box, Timer, Wrench, Repeat, Flag, CalendarDays, Layers, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GarageItemDialog } from "@/components/GarageItemDialog";
import { maintenanceCost } from "@/components/MaintenanceList";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { PageHeader } from "@/pages/Events";
import { useT } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useGarage } from "@/lib/useGarage";

const dateIt = (d) => (d ? d.split("-").reverse().join("/") : "");

const Stat = ({ Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-[#0D0F15] border border-[#282D3D] rounded-sm px-2.5 py-2 min-w-0">
    <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-widest text-slate-500 truncate"><Icon className="w-3 h-3 shrink-0" />{label}</div>
    <div className={`font-heading font-bold text-xl leading-tight tabular-nums truncate ${accent || "text-white"}`}>{value}</div>
  </div>
);

const GarageStats = ({ item, kind }) => {
  const { t } = useT();
  const u = item.usage || {};
  const rows = item.maintenance || [];
  const lastReset = rows.find((m) => m.reset_hours);
  const stats = [
    [Repeat, t("g_laps"), u.laps ?? 0, "text-[#FFB800]", "laps"],
    [Layers, t("g_sessions"), u.sessions ?? 0, null, "sessions"],
    [Flag, t("g_events"), u.events ?? 0, null, "events"],
    [CalendarDays, t("g_last_used"), u.last_used ? dateIt(u.last_used) : "—", null, "last-used"],
  ];
  if (kind === "engines") {
    stats.push([Timer, t("g_hours"), item.hour_meter != null ? `${item.hour_meter} h` : "—", "text-[#FFB800]", "hours"]);
    stats.push([RotateCcw, t("g_laps_reset"), u.laps_since_reset ?? "—", null, "laps-reset"]);
  }
  return (
    <>
      <div className={`grid grid-cols-2 gap-2 mt-3 ${kind === "engines" ? "sm:grid-cols-3" : "sm:grid-cols-4"}`}>
        {stats.map(([I, l, v, a, k]) => <Stat key={k} Icon={I} label={l} value={v} accent={a} testid={`garage-stat-${k}-${item.id}`} />)}
      </div>
      {kind === "engines" && (
        <div data-testid="engine-info" className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-slate-400">
          {item.carburetor && <span>{t("carburetor")}: <span className="text-slate-200">{item.carburetor}</span></span>}
          {item.ignition && <span>{t("ignition")}: <span className="text-slate-200">{item.ignition}</span></span>}
          {rows.length > 0 && (
            <span data-testid="engine-info-maintenance" className="flex items-center gap-1.5"><Wrench className="w-3.5 h-3.5" /> {rows.length} {t("maintenance_short")} · € {maintenanceCost(rows).toFixed(2)}{lastReset ? ` · ${t("last_reset")} ${dateIt(lastReset.date)}` : ""}</span>
          )}
          {item.hour_meter_date && <span data-testid="engine-info-hours">{t("g_hours")} @ {dateIt(item.hour_meter_date)}</span>}
        </div>
      )}
    </>
  );
};

const GarageItem = ({ item, kind, onEdit, onDelete }) => (
  <div data-testid={`garage-item-${item.id}`} className="fade-up relative bg-[#12141C] border border-[#282D3D] rounded-sm p-4 pl-5 hover:border-slate-600 transition-colors duration-200 overflow-hidden">
    <span className={`absolute left-0 inset-y-0 w-1 ${kind === "engines" ? "bg-[#FFB800]" : "bg-[#FF3B30]"}`} />
    <div className="flex items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <span data-testid="garage-item-name" className="font-heading font-bold uppercase text-xl leading-tight truncate">{item.name}</span>
          {item.serial && <span className="shrink-0 font-mono text-[10px] px-1.5 py-0.5 border border-[#FFB800]/40 text-[#FFB800] rounded-sm">#{item.serial}</span>}
        </div>
        <div className="font-mono text-xs text-slate-400 mt-0.5">{[item.brand, item.model].filter(Boolean).join(" · ") || "—"}</div>
      </div>
      <button data-testid={`garage-edit-${item.id}`} onClick={() => onEdit(item)} className="p-2 text-slate-500 hover:text-white rounded-sm hover:bg-[#282D3D] transition-colors duration-200"><Pencil className="w-4 h-4" /></button>
      <ConfirmDelete testid={`garage-delete-${item.id}`} onConfirm={() => onDelete(kind, item)} trigger={
        <button data-testid={`garage-delete-${item.id}-btn`} className="p-2 text-slate-500 hover:text-[#FF3B30] rounded-sm hover:bg-[#282D3D] transition-colors duration-200"><Trash2 className="w-4 h-4" /></button>
      } />
    </div>
    <GarageStats item={item} kind={kind} />
    {item.notes && <p className="text-sm text-slate-500 mt-2.5 line-clamp-2">{item.notes}</p>}
  </div>
);

const GarageColumn = ({ kind, title, Icon, items, onAdd, onEdit, onDelete }) => {
  const { t } = useT();
  return (
    <section data-testid={`garage-section-${kind}`}>
      <div className="flex items-center justify-between mb-4 border-b border-[#282D3D] pb-3">
        <h2 className="font-heading font-bold uppercase text-2xl sm:text-3xl flex items-center gap-2">
          <Icon className="w-5 h-5 text-[#FF3B30]" /> {title} <span className="font-mono text-base text-slate-500">[{items.length}]</span>
        </h2>
        <Button data-testid={`garage-add-${kind}-btn`} onClick={() => onAdd(kind)} variant="outline" className="rounded-sm bg-transparent border-[#282D3D] hover:bg-[#181B26] hover:text-white">
          <Plus className="w-4 h-4 mr-1" /> {t("add")}
        </Button>
      </div>
      <div className="space-y-2.5">
        {items.length === 0 && <div data-testid={`garage-empty-${kind}`} className="border border-dashed border-[#282D3D] rounded-sm p-8 text-center text-slate-500 font-mono text-sm">{t("no_items")}</div>}
        {items.map((it) => <GarageItem key={it.id} item={it} kind={kind} onEdit={(i) => onEdit(kind, i)} onDelete={onDelete} />)}
      </div>
    </section>
  );
};

export default function Garage() {
  const { t } = useT();
  const { garage, reload } = useGarage();
  const [dialog, setDialog] = useState({ open: false, kind: "engines", item: null });

  const onDelete = async (kind, item) => {
    await api.delete(`/garage/${kind}/${item.id}`);
    toast.success(t("deleted"));
    reload();
  };
  const props = {
    onAdd: (kind) => setDialog({ open: true, kind, item: null }),
    onEdit: (kind, item) => setDialog({ open: true, kind, item }),
    onDelete,
  };

  return (
    <div>
      <PageHeader title={t("garage_title")} sub={t("garage_sub")} image="/images/helmet.jpg" imagePos="center 22%" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <GarageColumn kind="chassis" title={t("chassis_plural")} Icon={Box} items={garage.chassis} {...props} />
        <GarageColumn kind="engines" title={t("engines")} Icon={Cog} items={garage.engines} {...props} />
      </div>
      <GarageItemDialog open={dialog.open} kind={dialog.kind} item={dialog.item} onSaved={reload}
        onOpenChange={(o) => setDialog({ ...dialog, open: o })} />
    </div>
  );
}
